import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNotificationInbox } from '../context/NotificationInboxContext';
import { formatNotificationTime, notificationMeta } from '../utils/notificationInbox';

function FilterTab({ active, label, count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
        active
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750'
      }`}
    >
      {label}
      {count != null && count > 0 && (
        <span className={`ml-1.5 ${active ? 'text-blue-100' : 'text-gray-400'}`}>({count})</span>
      )}
    </button>
  );
}

function NotificationCard({ item, onRead, onDelete }) {
  const meta = notificationMeta(item.type);
  const navigate = useNavigate();

  const open = () => {
    if (!item.read) onRead(item._id);
    if (item.link) navigate(item.link);
  };

  return (
    <article
      className={`rounded-xl border transition-all ${
        item.read
          ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80'
          : 'border-blue-200 dark:border-blue-800/50 bg-blue-50/40 dark:bg-blue-950/20 shadow-sm'
      }`}
    >
      <div className="p-4 sm:p-5 flex gap-4">
        <span
          className={`shrink-0 w-11 h-11 rounded-xl flex items-center justify-center text-xl ${meta.bg}`}
          aria-hidden
        >
          {meta.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
            <h2 className="text-base font-bold text-gray-900 dark:text-white">{item.title}</h2>
            <time className="text-xs text-gray-400 dark:text-gray-500 shrink-0">
              {formatNotificationTime(item.createdAt)}
            </time>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{item.message}</p>
          <div className="flex flex-wrap items-center gap-2 mt-4">
            {item.link && (
              <button
                type="button"
                onClick={open}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Open
              </button>
            )}
            {!item.read && (
              <button
                type="button"
                onClick={() => onRead(item._id)}
                className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Mark read
              </button>
            )}
            <button
              type="button"
              onClick={() => onDelete(item._id)}
              className="inline-flex items-center px-3 py-1.5 rounded-lg text-xs font-semibold text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function EmailNotificationSettings() {
  const { user, refreshUser } = useAuth();
  const [enabled, setEnabled] = useState(Boolean(user?.emailNotificationsEnabled));
  const [hasEmail, setHasEmail] = useState(Boolean(user?.hasEmail ?? user?.email));
  const [email, setEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api
      .get('/notifications/preferences')
      .then(({ data }) => {
        setEnabled(Boolean(data?.emailNotificationsEnabled));
        setHasEmail(Boolean(data?.hasEmail));
        setEmail(data?.email || '');
      })
      .catch(() => {});
  }, []);

  const handleToggle = async () => {
    if (!hasEmail && !enabled) {
      setMessage('Add an email to your account (Profile or sign up with email) to enable mail notifications.');
      return;
    }
    const next = !enabled;
    setSaving(true);
    setMessage('');
    try {
      const { data } = await api.patch('/notifications/preferences/email', { enabled: next });
      setEnabled(Boolean(data?.emailNotificationsEnabled));
      setHasEmail(Boolean(data?.hasEmail));
      setEmail(data?.email || '');
      if (refreshUser) {
        await refreshUser();
      }
      setMessage(next ? 'Email notifications enabled.' : 'Email notifications turned off.');
    } catch (err) {
      setMessage(err?.response?.data?.message || 'Could not update email preferences.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 sm:p-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span aria-hidden>📧</span>
            Email notifications
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
            Off by default. Turn on to receive copies of important alerts — tickets, game updates, results, and reminders.
          </p>
          {hasEmail && email && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Sends to <span className="font-medium text-gray-600 dark:text-gray-300">{email}</span>
            </p>
          )}
          {!hasEmail && (
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-2">
              Wallet-only account — link or add an email on your profile to use mail notifications.
            </p>
          )}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          disabled={saving || (!hasEmail && !enabled)}
          onClick={handleToggle}
          className={`relative shrink-0 inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 ${
            enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
          } ${!hasEmail && !enabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span
            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      {message && (
        <p className={`mt-3 text-sm ${message.includes('Could not') || message.includes('Add an email') ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
          {message}
        </p>
      )}
    </div>
  );
}

export default function Notifications() {
  const { unreadCount, markRead, markAllRead, deleteNotification, fetchUnreadCount } = useNotificationInbox();
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const limit = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', {
        params: {
          page,
          limit,
          unread: filter === 'unread' ? 'true' : undefined,
        },
      });
      setItems(data?.items || []);
      setTotal(data?.total || 0);
      if (typeof data?.unreadCount === 'number') fetchUnreadCount();
    } catch {
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, filter, fetchUnreadCount]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const handleRead = async (id) => {
    await markRead(id);
    setItems((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
  };

  const handleDelete = async (id) => {
    await deleteNotification(id);
    setItems((prev) => prev.filter((n) => n._id !== id));
    setTotal((t) => Math.max(0, t - 1));
  };

  const handleClearRead = async () => {
    try {
      await api.delete('/notifications/read');
      load();
    } catch {
      /* ignore */
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Link to="/" className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-2 inline-block">
            ← Back to home
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black text-gray-900 dark:text-white">Notifications</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Ticket rewards, game updates, results, and reminders — all in one place.
          </p>
        </div>

        <EmailNotificationSettings />

        <div className="flex flex-wrap items-center gap-2 mb-6">
          <FilterTab active={filter === 'all'} label="All" onClick={() => setFilter('all')} />
          <FilterTab active={filter === 'unread'} label="Unread" count={unreadCount} onClick={() => setFilter('unread')} />
          <div className="flex-1" />
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={async () => {
                await markAllRead();
                setItems((prev) => prev.map((n) => ({ ...n, read: true })));
              }}
              className="text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
            >
              Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={handleClearRead}
            className="text-sm font-semibold text-gray-500 dark:text-gray-400 hover:underline"
          >
            Clear read
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800/50 p-12 text-center">
            <p className="text-4xl mb-3" aria-hidden>
              🔔
            </p>
            <p className="font-semibold text-gray-800 dark:text-gray-200">
              {filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto">
              When you receive tickets, new games go live, or results are posted, they will show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <NotificationCard key={item._id} item={item} onRead={handleRead} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 mt-8">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-white dark:hover:bg-gray-800"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-white dark:hover:bg-gray-800"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
